import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { subscribeInstall, promptInstall } from "../pwa";

/**
 * Small, unobtrusive "Install app" button.
 * - Only renders when the browser fires beforeinstallprompt (Chromium/Android/desktop).
 * - Auto-hides when the app is already installed / launched from home screen.
 * - variants: "sidebar" (compact) | "card" (larger CTA for Member Home).
 */
export default function InstallAppButton({ variant = "sidebar", className = "" }) {
  const [state, setState] = useState({ canInstall: false, isStandalone: false });

  useEffect(() => subscribeInstall(setState), []);

  if (state.isStandalone || !state.canInstall) return null;

  const onClick = async () => {
    const res = await promptInstall();
    if (res.outcome === "accepted") toast.success("StUpEco Hub installed.");
  };

  if (variant === "card") {
    return (
      <div
        data-testid="install-app-card"
        className={`hub-card flex items-center gap-4 rounded-2xl p-4 ${className}`}
      >
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[color:var(--brand-teal)]/10 text-[color:var(--brand-teal)]">
          <Download className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-sm font-semibold text-[color:var(--ink)]">Install StUpEco Hub</p>
          <p className="text-xs text-[color:var(--ink-soft)]">One-tap access from your home screen.</p>
        </div>
        <button
          onClick={onClick}
          data-testid="install-app-card-btn"
          className="rounded-full bg-[color:var(--invert-bg)] px-4 py-2 text-xs font-semibold text-white transition-transform hover:-translate-y-0.5"
        >
          Install
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={onClick}
      data-testid="install-app-sidebar-btn"
      className={`inline-flex w-full items-center justify-between gap-2 rounded-xl border border-dashed border-[color:var(--brand-teal)]/50 bg-[color:var(--brand-teal)]/5 px-3 py-2 text-xs font-semibold text-[color:var(--brand-teal)] transition-colors hover:bg-[color:var(--brand-teal)]/10 ${className}`}
      title="Install StUpEco Hub on your device"
    >
      <span className="inline-flex items-center gap-2">
        <Download className="h-3.5 w-3.5" /> Install app
      </span>
      <span className="rounded-full bg-[color:var(--brand-teal)]/10 px-2 py-0.5 text-[9px] uppercase tracking-wide">Beta</span>
    </button>
  );
}
